import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Dialog, makeStyles, Divider, withStyles, Button, Theme, createStyles, TextField} from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import {useDispatch, useSelector} from "react-redux";
import {createProject, updateProject} from "../../../store/actions/creator/creatorDashboard";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      paddingTop: 26
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      background: 'transparent!important'
    },
    header: {
      color: '#212D40',
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      paddingLeft: 17,
      paddingBottom: 7,
      display: 'flex',
      alignItems: 'center'
    }
  });

const useStyles = makeStyles(() => ({
  root: {},
  paper: {
    borderRadius: '24px!important'
  },
  createBtn: {
    width: 91,
    height: 37,
    borderRadius: 6,
    background: '#0179EE',
    color: '#FFFFFF',
    textTransform: 'none',
    fontSize: 14,
    fontFamily: 'roboto-regular',
    '&:hover': {
      background: '#0f9fef'
    },
  },
  cancelBtn: {
    width: 91,
    height: 37,
    borderRadius: 6,
    background: '#F0F0F0',
    color: '#697683',
    textTransform: 'none',
    fontSize: 14,
    fontFamily: 'roboto-regular',
    '&:hover': {
      background: '#e0e0e0',
    },
  },
  textField: {
    "& .MuiOutlinedInput-inputMarginDense": {
      margin: "0!important",
      border: '1px solid #697683',
      padding: 4,
      borderRadius: 3,
      paddingLeft: 6,
      fontSize: 14,
      color: '#717E8B',
    }
  },
  label: {
    "&$focusedLabel": {
      fontSize: 12,
      fontFamily: 'roboto-bold',
      color: "#606B76"
    },
    "&$erroredLabel": {
      color: "#707070"
    }
  },
  focusedLabel: {},
  erroredLabel: {},
  underline: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    "&$error:after": {
      borderBottomColor: "#017AEF"
    },
    "&:after": {
      borderBottom: `3px solid #017AEF`
    }
  },
  error: {},
  description: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683'
  },
  input: {
    marginTop: 50
  }
}));

const DialogTitle = withStyles(styles)((props: any) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <div className={classes.header}>
        {children}
        {onClose ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <img src={'/assets/icons/creatordashboard/project-modal-close.svg'} alt={'img'}/>
          </IconButton>
        ) : null}
      </div>
    </MuiDialogTitle>
  );
});

ProjectModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  selectedIndex: PropTypes.number
};

ProjectModal.defaultProps = {
  onClose: () => {
  }
};

function ProjectModal({open, onClose, selectedIndex, ...rest}: any) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {projects} = useSelector((state: any) => state.creatorDashboard);
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : ""
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (selectedIndex >= 0) {
      setProjectName(projects[selectedIndex].projectName)
    }
  }, [selectedIndex, projects]);

  const onCloseDialog = () => {
    onClose();
    setProjectName('');
  };

  const onAddProject = () => {
    if (validateProjectName(projectName)) {
      const request = {
        cascadedProjectOrder: projects.length,
        cascadedProjectId: 0,
        projectOwnerId: Number(loginUserData.user_id),
        departmentId: 0,
        iconStimulusId: 0,
        backgroundStimulusId: 0,
        tagCloud: {},
        projectDescription: "",
        projectName,
        projectAlias: ""
      };
      dispatch(createProject(apolloClient.client, request));
      onCloseDialog()
    }
  };

  const onUpdateProject = () => {
    if (validateProjectName(projectName)) {
      const selectedProject = projects[selectedIndex];
      const request = {
        ...selectedProject,
        projectName
      };
      dispatch(updateProject(apolloClient.client, request, selectedIndex));
      onCloseDialog();
    }
  };

  const onChangeProjectName = (event: any) => {
    setProjectName(event.target.value);
  };

  const validateProjectName = (name: string) => {
    return name.length > 1
  };

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth={true}
      maxWidth={'sm'}
      open={open}
      classes={{
        root: classes.root,
        paper: classes.paper
      }}
    >
      <div {...rest} >
        <DialogTitle onClose={onCloseDialog}>
          {selectedIndex >= 0 ? 'Rename' : 'Create a New Project'}
        </DialogTitle>
        <Divider/>

        <Box m={4} pl={1} pr={1}>
          <p className={classes.description}>
            Give your project a name. Then to add items to your project, go to All Experiences and select the items you
            want to add.
          </p>
          <TextField
            InputLabelProps={{
              classes: {
                root: classes.label,
                focused: classes.focusedLabel,
                error: classes.erroredLabel
              },
            }}
            InputProps={{
              classes: {
                root: classes.underline,
                error: classes.error
              }
            }}
            value={projectName}
            fullWidth
            margin="normal"
            id="project_name"
            label="Project Name"
            name="project_name"
            onChange={onChangeProjectName}
          />
        </Box>
        <Box mt={6} mb={3} mr={4} display='flex' justifyContent='flex-end'>
          <Button onClick={onCloseDialog} className={classes.cancelBtn}>
            Cancel
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button
            onClick={() => selectedIndex >= 0 ? onUpdateProject() : onAddProject()}
            className={classes.createBtn}
            disabled={!validateProjectName(projectName)}
            style={!validateProjectName(projectName) ? {backgroundColor: "#F0F0F0"} : {}}
          >
            {selectedIndex >= 0 ? 'Update' : 'Create'}
          </Button>
        </Box>
      </div>
    </Dialog>
  );
}

export default ProjectModal;
